import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { getDexChains, getDexDiffPct, hasDexPriceData } from './dexMonitorModel';
import type { DexPricesResponse } from '../types/trade';

const CHAIN_LABELS: Record<string, string> = {
  bsc: 'BSC',
  tron: 'Tron',
  ethereum: 'Ethereum',
  polygon: 'Polygon',
};

function formatAge(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

function formatPrice(price: number): string {
  if (price < 0.001) return price.toFixed(8);
  if (price < 1) return price.toFixed(6);
  return price.toFixed(4);
}

export function DexMonitor() {
  const [data, setData] = useState<DexPricesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const result = await api.getDexPrices();
      if (result.status === 'error') {
        setError('API error');
        setLoading(false);
        return;
      }
      setData(result);
      setError(null);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load DEX prices');
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.container}><div style={styles.loading}>Loading DEX prices...</div></div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>DEX Monitor</h3>
        </div>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!data || !hasDexPriceData(data)) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>DEX Monitor</h3>
        </div>
        <div style={styles.empty}>
          No DEX price data available.
          <br />
          <span style={styles.emptyDetail}>
            DEX tracker may be paused — data older than 1 hour is excluded.
          </span>
        </div>
      </div>
    );
  }

  const chains = getDexChains(data);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>DEX Monitor</h3>
        <div style={styles.headerRight}>
          {data.cex_price && (
            <span style={styles.cexBadge}>
              CEX: ${formatPrice(data.cex_price)}
            </span>
          )}
          {lastUpdate && (
            <span style={styles.updateTime}>
              Updated {formatAge((Date.now() - lastUpdate.getTime()) / 1000)} ago
            </span>
          )}
        </div>
      </div>

      {/* Summary bar */}
      {data.summary.count && data.summary.count > 0 && (
        <div style={styles.summaryBar}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Avg DEX</span>
            <span style={styles.summaryValue}>${formatPrice(data.summary.average!)}</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Min</span>
            <span style={styles.summaryValue}>${formatPrice(data.summary.min!)}</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Max</span>
            <span style={styles.summaryValue}>${formatPrice(data.summary.max!)}</span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Sources</span>
            <span style={styles.summaryValue}>{data.summary.count}</span>
          </div>
          {data.cex_price && data.summary.average && (
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>CEX-DEX Diff</span>
              <span style={{
                ...styles.summaryValue,
                color: Math.abs(((data.summary.average - data.cex_price) / data.cex_price) * 100) > 2
                  ? '#ef4444' : '#22c55e',
              }}>
                {(((data.summary.average - data.cex_price) / data.cex_price) * 100).toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* Price table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Chain</th>
            <th style={styles.th}>DEX</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Price</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>vs CEX</th>
            <th style={{ ...styles.th, textAlign: 'right' }}>Age</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {chains.map(chain =>
            Object.entries(data.dex_prices[chain]).map(([dex, entry]) => {
              const diffPct = getDexDiffPct(entry, data.cex_price);
              const isStale = entry.age_seconds > 300;
              const diffColor = diffPct === null
                ? '#94a3b8'
                : Math.abs(diffPct) > 5
                  ? '#ef4444'
                  : Math.abs(diffPct) > 2
                    ? '#f59e0b'
                    : '#22c55e';

              return (
                <tr key={`${chain}-${dex}`} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={styles.chainBadge}>
                      {CHAIN_LABELS[chain] || chain.toUpperCase()}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.dexName}>{dex.replace(/_/g, ' ')}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'monospace' }}>
                    ${formatPrice(entry.price)}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', color: diffColor, fontFamily: 'monospace' }}>
                    {diffPct !== null ? `${diffPct >= 0 ? '+' : ''}${diffPct.toFixed(2)}%` : '--'}
                  </td>
                  <td style={{
                    ...styles.td,
                    textAlign: 'right',
                    color: isStale ? '#f59e0b' : '#94a3b8',
                  }}>
                    {formatAge(entry.age_seconds)}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <span style={{
                      ...styles.statusDot,
                      backgroundColor: entry.fresh ? '#22c55e' : isStale ? '#ef4444' : '#f59e0b',
                    }} />
                    <span style={{
                      fontSize: '11px',
                      color: entry.fresh ? '#22c55e' : isStale ? '#ef4444' : '#f59e0b',
                    }}>
                      {entry.fresh ? 'Live' : isStale ? 'Stale' : 'Delayed'}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {/* Arbitrage opportunities */}
      {data.arbitrage.length > 0 && (
        <div style={styles.arbSection}>
          <h4 style={styles.arbTitle}>Arbitrage Opportunities</h4>
          {data.arbitrage.map((arb, i) => (
            <div key={i} style={styles.arbRow}>
              <span style={styles.arbDirection}>
                {arb.type === 'cex_to_dex' ? 'CEX \u2192 DEX' : 'DEX \u2192 CEX'}
              </span>
              <span style={styles.arbChain}>
                {CHAIN_LABELS[arb.chain] || arb.chain.toUpperCase()} / {arb.dex.replace(/_/g, ' ')}
              </span>
              <span style={styles.arbProfit}>+{arb.profit_pct.toFixed(2)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#1e1e2e',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #313244',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#cdd6f4',
  },
  cexBadge: {
    fontSize: '12px',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#313244',
    color: '#89b4fa',
    fontFamily: 'monospace',
  },
  updateTime: {
    fontSize: '11px',
    color: '#6c7086',
  },
  loading: {
    color: '#6c7086',
    textAlign: 'center' as const,
    padding: '24px',
  },
  error: {
    color: '#ef4444',
    textAlign: 'center' as const,
    padding: '16px',
    fontSize: '13px',
  },
  empty: {
    color: '#6c7086',
    textAlign: 'center' as const,
    padding: '24px',
    fontSize: '13px',
    lineHeight: '1.6',
  },
  emptyDetail: {
    fontSize: '11px',
    color: '#585b70',
  },
  summaryBar: {
    display: 'flex',
    gap: '16px',
    padding: '8px 12px',
    backgroundColor: '#181825',
    borderRadius: '6px',
    marginBottom: '12px',
    flexWrap: 'wrap' as const,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  summaryLabel: {
    fontSize: '10px',
    color: '#6c7086',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  summaryValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#cdd6f4',
    fontFamily: 'monospace',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    borderBottom: '1px solid #313244',
    color: '#6c7086',
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #1e1e2e',
  },
  td: {
    padding: '8px 12px',
    color: '#cdd6f4',
    borderBottom: '1px solid #232334',
  },
  chainBadge: {
    fontSize: '11px',
    padding: '2px 6px',
    borderRadius: '3px',
    backgroundColor: '#313244',
    color: '#a6adc8',
    fontWeight: 500,
  },
  dexName: {
    textTransform: 'capitalize' as const,
    color: '#bac2de',
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle',
  },
  arbSection: {
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: '#181825',
    borderRadius: '6px',
    border: '1px solid #45475a',
  },
  arbTitle: {
    margin: '0 0 8px 0',
    fontSize: '12px',
    fontWeight: 600,
    color: '#f9e2af',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  arbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '4px 0',
    fontSize: '12px',
  },
  arbDirection: {
    color: '#89b4fa',
    fontWeight: 500,
    minWidth: '90px',
  },
  arbChain: {
    color: '#a6adc8',
    flex: 1,
  },
  arbProfit: {
    color: '#22c55e',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
};
